import { Link } from 'react-router';
import { useStore } from '../store/useStore';
import { trpc } from '../trpc/client';
import { Heart, ShoppingCart, Star, Trash2, ArrowRight } from 'lucide-react';

export default function Wishlist() {
  const { currency } = useStore();
  const { data: wishlistData, isLoading } = trpc.deal.getWishlist.useQuery({});
  const utils = trpc.useContext();
  const toggleWishlist = trpc.deal.toggleWishlist.useMutation({
    onSuccess: () => {
      utils.deal.getWishlist.invalidate();
    },
  });

  const deals = wishlistData?.deals || [];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-[#1e293b] font-display">My Wishlist</h1>
          <p className="text-[#64748b]">{deals.length} saved deals</p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1e293b] mb-2">Your Wishlist is Empty</h2>
            <p className="text-[#64748b] mb-6">Save deals you're interested in for later</p>
            <Link to="/deals" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold inline-flex items-center gap-2">
              Browse Deals <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {deals.map((deal: any) => (
              <div key={deal.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 group hover:shadow-lg transition-all hover:-translate-y-1">
                <Link to={`/deals/${deal.slug}`} className="block relative h-48 overflow-hidden">
                  <img src={deal.images?.[0]} alt={deal.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </Link>
                <div className="p-4">
                  <p className="text-[10px] font-semibold text-[#c2410c] uppercase tracking-wider mb-1">{deal.category?.name}</p>
                  <Link to={`/deals/${deal.slug}`} className="text-sm font-semibold text-[#1e293b] line-clamp-2 group-hover:text-[#c2410c] transition-colors block mb-2">
                    {deal.name}
                  </Link>
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : '€'}{Number(deal.buyerPrice).toLocaleString()}</p>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <span className="text-xs font-semibold">{deal.avgRating}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => toggleWishlist.mutate({ dealId: deal.id })}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-red-200 text-red-500 rounded-lg text-xs font-medium hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                    <Link to={`/deals/${deal.slug}`} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#c2410c] text-white rounded-lg text-xs font-medium hover:bg-[#9a3412] transition-colors">
                      <ShoppingCart className="w-3.5 h-3.5" /> View
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
